import { z } from 'zod';

export const STAFF_ROLES = ['admin', 'support'] as const;
export type StaffRole = (typeof STAFF_ROLES)[number];
export const staffRoleSchema = z.enum(STAFF_ROLES);

/** Etykiety ról po polsku — do UI. */
export const STAFF_ROLE_LABELS_PL: Readonly<Record<StaffRole, string>> = {
  admin: 'Administrator',
  support: 'Pracownik supportu',
};

/**
 * E-mail zawsze sprowadzany do małych liter i przycinany. Bez tego
 * „Jan@Firma.pl" i „jan@firma.pl" byłyby dwoma kontami mimo UNIQUE w bazie.
 */
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(254, 'Adres e-mail jest za długi.')
  .pipe(z.email('Nieprawidłowy adres e-mail.'));

/**
 * Zgodnie z zaleceniami NIST stawiamy na DŁUGOŚĆ, nie na wymuszanie
 * znaków specjalnych — te ostatnie produkują „Haslo1!" i karteczki pod
 * klawiaturą, nie realną entropię.
 */
export const MIN_PASSWORD_LENGTH = 12;
export const MAX_PASSWORD_LENGTH = 128;

export const passwordSchema = z
  .string()
  .min(MIN_PASSWORD_LENGTH, `Hasło musi mieć co najmniej ${MIN_PASSWORD_LENGTH} znaków.`)
  // Górny limit, bo hashowanie długiego wejścia to darmowy wektor DoS-u
  // przy 600 000 iteracji PBKDF2.
  .max(MAX_PASSWORD_LENGTH, `Hasło może mieć najwyżej ${MAX_PASSWORD_LENGTH} znaków.`);

export const loginSchema = z.object({
  email: emailSchema,
  // Przy logowaniu NIE stosujemy reguł siły hasła — inaczej formularz
  // zdradzałby politykę i odrzucałby stare, krótsze hasła zamiast
  // po prostu ich nie uznać.
  password: z.string().min(1, 'Podaj hasło.').max(MAX_PASSWORD_LENGTH),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const createStaffUserSchema = z.object({
  email: emailSchema,
  displayName: z.string().trim().min(1, 'Podaj imię i nazwisko.').max(120),
  role: staffRoleSchema.default('support'),
  password: passwordSchema,
});
export type CreateStaffUserInput = z.infer<typeof createStaffUserSchema>;

export const updateStaffUserSchema = z
  .object({
    displayName: z.string().trim().min(1).max(120).optional(),
    role: staffRoleSchema.optional(),
    isActive: z.boolean().optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'Nie podano żadnej zmiany.',
  });
export type UpdateStaffUserInput = z.infer<typeof updateStaffUserSchema>;

/** Reset hasła przez administratora — bez znajomości starego hasła. */
export const resetStaffPasswordSchema = z.object({ password: passwordSchema });

/** Zmiana własnego hasła — wymaga potwierdzenia obecnym. */
export const changeOwnPasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Podaj obecne hasło.').max(MAX_PASSWORD_LENGTH),
  newPassword: passwordSchema,
});
export type ChangeOwnPasswordInput = z.infer<typeof changeOwnPasswordSchema>;

/**
 * Reprezentacja pracownika wychodząca na zewnątrz.
 * Nigdy nie zawiera hasza, soli ani liczby iteracji.
 */
export const staffUserPublicSchema = z.object({
  id: z.string(),
  email: z.string(),
  displayName: z.string(),
  role: staffRoleSchema,
  isActive: z.boolean(),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
});
export type StaffUserPublic = z.infer<typeof staffUserPublicSchema>;

/** Odpowiedź `GET /api/auth/me`. */
export const currentUserSchema = z.object({
  user: staffUserPublicSchema,
  /** Kiedy sesja wygaśnie bezwzględnie, niezależnie od aktywności. */
  sessionExpiresAt: z.string(),
});
export type CurrentUser = z.infer<typeof currentUserSchema>;
